/*
 * Sidebar
 * @module components
 * @flow
 */

import React from 'react';
import posed from 'react-pose';
import styled, { css } from 'styled-components/macro';
import { palette } from 'styled-theme';
import { ifProp } from 'styled-tools';
import { FormattedMessage } from 'react-intl';
import { Mutation } from 'react-apollo';

import { LanguageSelect } from '~/components/LanguageSelect';
import { SidebarItem, SidebarUser } from './components';
import { Tabs } from '~/components/Tabs';
import { SET_NAVIGATION_VISIBILITY } from '~/gql';

// $FlowIssue
import { ReactComponent as IconArrow } from '@/icons/ic-toggle-sidebar.svg';
// $FlowIssue
import { ReactComponent as IconLogo } from '@/icons/grc-logo.svg';
// $FlowIssue
import { ReactComponent as IconLogoBig } from '@/icons/grc-logo-big.svg';

import messages from './messages';

export type SidebarProps = {
  children: Object => React$Node,
  open?: boolean,
  onLogoClick: () => void,
  link: React$Node,
};

const baseColor = palette('grayscale', 0, true);

const SidebarAnimation = posed.div({
  close: {
    width: '56px',
  },
  open: {
    width: '189px',
    beforeChildren: true,
    staggerChildren: 50,
  },
});

const SidebarWrapper = styled(SidebarAnimation)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 20px;
  height: 100%;
  background-color: ${palette(1)};
  box-sizing: border-box;
  /* stylelint-disable-next-line property-no-unknown */
  webkit-overflow-scrolling: touch;
`;

const SidebarTabs = styled(Tabs)`
  padding: 0;
  width: 100%;
  min-height: ${ifProp('open', '260px', '310px')};
  background: transparent;
  box-shadow: none;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  overflow: auto;
`;

const SidebarGRCLogoBig = styled(IconLogoBig)`
  margin-bottom: 12px;

  ${ifProp(
    'onClick',
    css`
      cursor: pointer;
    `,
  )};
`;

const SidebarGRCLogo = styled(IconLogo)`
  width: 56px;

  ${ifProp(
    'onClick',
    css`
      cursor: pointer;
    `,
  )};
`;

const SidebarToggleButton = styled(IconArrow)`
  color: ${baseColor};
  opacity: 0.8;
  transition: transform 0.3s;

  ${ifProp(
    'open',
    css`
      left: 8px;
    `,
    css`
      right: 8px;
      transform: rotate(180deg);
    `,
  )};
`;

const SidebarLanguageSelect = styled.div`
  display: flex;
  flex-wrap: ${ifProp('open', 'no-wrap', 'wrap')};
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  .react-select {
    margin-bottom: 16px;
    width: 50px;
  }
`;

/**
 * Sidebar React-component
 * @memberof components * @param {Object} props - properties
 * @param {React.Children} [props.children] - children
 * @param {boolean} [props.open] - sidebar open state
 * @param {Function} [props.onLogoClick] - logo click handler
 * @example <caption>Simple usage</caption>
 * <Sidebar><SidebarItem icon={<IconWallet />}>My Wealth</SidebarItem></Sidebar>
 * @returns {React.Node<*>} React-component
 */
export const Sidebar = ({
  children,
  open,
  onLogoClick,
  link,
  ...rest
}: SidebarProps) => {
  const pose = open ? 'open' : 'close';
  const SidebarGRCLogoComponent = open ? SidebarGRCLogoBig : SidebarGRCLogo;

  return (
    <Mutation
      mutation={SET_NAVIGATION_VISIBILITY}
      variables={{ isVisible: !open }}
    >
      {toggleSidebar => (
        <SidebarWrapper {...rest} initialPose="close" pose={pose}>
          <SidebarUser open={open} {...rest} />
          <SidebarTabs open={open}>{children({ open })}</SidebarTabs>
          <SidebarLanguageSelect open={open}>
            <LanguageSelect />
            {link}
          </SidebarLanguageSelect>
          <SidebarGRCLogoComponent onClick={onLogoClick} />
          <SidebarItem
            icon={<SidebarToggleButton open={open} {...rest} />}
            open={open}
            onClick={toggleSidebar}
            {...rest}
          >
            <FormattedMessage {...messages.collapse} />
          </SidebarItem>
        </SidebarWrapper>
      )}
    </Mutation>
  );
};

Sidebar.defaultProps = {
  palette: 'primary',
  onLogoClick: () => {},
};
